import React from 'react';
import { Box, Divider } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { FieldValues, useWatch, useFormContext, Path, PathValue } from 'react-hook-form';
import {
  AggregationsSchema,
  BeaconFilterFieldTypeSchema,
  MetricAdjustmentsSchema,
  MetricFilterOperatorSchema,
  KeywordSensibleMetricsSchema,
  ResponseTypesSchema
} from '../schema/AlertBuilderSchema';
import { AggregationsType, OperatorConfigType, BeaconFilterFieldTypeType, CreateAlertConfigType } from '../schema/AlertBuilderSchemaTypes';
import PathControlledDropdown from './ui/PathControlledDropdown';
import PathControlledBooleanDropdown from './ui/PathControlledBooleanDropdown';
import ExpandCollapseIcon from './ui/ExpandCollapseIcon';
import DeleteIcon from './ui/DeleteIcon';
import RuleButton from './RuleButton';
import RuleBlockTimeRange from './RuleBlockTimeRange';
import colors from 'src/utils/colors';
import CategoricalFilterSection from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/CategoricalFilterSection';
import {
  DEFAULT_LOGICAL_OPERATOR,
  createDefaultCategoricalFilter,
  SINGLETON_CATEGORICAL_TYPES,
  isAddCategoricalDisabled,
  DROPDOWN_WIDTH,
  generateTimePeriodDescription
} from '../utils';
import MetricAdjustmentSection from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/MetricAdjustmentSection';
import MetricFilterSection from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/MetricFilterSection';

type Props<T extends FieldValues> = {
  groupIndex: number;
  blockIndex: number;
  metricsOptions: string[];
};

function RuleBlock<T extends FieldValues = CreateAlertConfigType>({
  groupIndex,
  blockIndex,
  metricsOptions
}: Props<T>) {
  const { setValue } = useFormContext<T>();

  // Base paths for this rule block
  const basePath = `queryJSON.rules.ruleGroups.${groupIndex}.ruleBlocks.${blockIndex}` as Path<T>;
  const categoricalFiltersPath = `${basePath}.categoricalfilters` as Path<T>;
  const metricAdjustmentPath = `${basePath}.metricAdjustment` as Path<T>;
  const metricFilterPath = `${basePath}.metricFilter` as Path<T>;

  // Paths for group-level data (for deletion logic)
  const groupBasePath = `queryJSON.rules.ruleGroups.${groupIndex}` as Path<T>;
  const ruleBlocksPath = `${groupBasePath}.ruleBlocks` as Path<T>;
  const operatorsPath = `${groupBasePath}.operators` as Path<T>;

  // Watch the block data and group data
  const block = useWatch({ name: basePath });
  const group = useWatch({ name: groupBasePath });
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const hasMetricAdjustment = Boolean(block?.metricAdjustment);
  const hasMetricFilter = Boolean(block?.metricFilter);

  // Watch for 'For the First Time' mode to show validation error
  const timeConfig = useWatch({ name: `${basePath}.timeConfiguration` as Path<T> });
  const isForTheFirstTimeMode = timeConfig?.mode === 'For the First Time';
  const shouldShowMetricFilterError = isForTheFirstTimeMode && !hasMetricFilter;

  // Watch for granularity and metric to show keyword sensible metric validation error
  const granularity = useWatch({ name: 'queryJSON.basicInfo.granularity' as Path<T> });
  const currentMetric = useWatch({ name: `${basePath}.metric` as Path<T> });
  const hasSearchtermGranularity = granularity?.includes(ResponseTypesSchema.enum['Searchterm']);
  const isMetricKeywordSensible = currentMetric ? KeywordSensibleMetricsSchema.safeParse(currentMetric).success : true;
  const shouldShowMetricSelectionError = hasSearchtermGranularity && currentMetric && !isMetricKeywordSensible;

  // Delete this rule block
  const deleteRuleBlock = () => {
    const updatedRuleBlocks = group.ruleBlocks.filter((ruleBlock, index) => index !== blockIndex);
    setValue(ruleBlocksPath, updatedRuleBlocks as PathValue<T, typeof ruleBlocksPath>);

    // Rebuild operators array with correct IDs and names
    if (updatedRuleBlocks.length > 1) {
      const updatedOperators = updatedRuleBlocks.slice(1).map((ruleBlock, index) => ({
        type: group.operators[index]?.type || DEFAULT_LOGICAL_OPERATOR
      }));
      setValue(operatorsPath, updatedOperators as PathValue<T, typeof operatorsPath>);

    } else {
      setValue(operatorsPath, [] as PathValue<T, typeof operatorsPath>);
    }
  };

  // Add a new categorical filter
  const addCategoricalFilter = () => {
    const currentFilters = block.categoricalfilters || [];
    const newFilter = createDefaultCategoricalFilter();
    const updatedFilters = [...currentFilters, newFilter];
    setValue(categoricalFiltersPath, updatedFilters as PathValue<T, typeof categoricalFiltersPath>);
  };

  // Add metric adjustment: initialize minimal shape per MetricAdjustmentSchema (non-reference mode)
  const addMetricAdjustment = () => {
    const defaultAdjustment = {
      operation: MetricAdjustmentsSchema.enum['Divide by'],
      useReferenceFilter: false,
      adjustmentValue: 1
    } as PathValue<T, typeof metricAdjustmentPath>;
    setValue(metricAdjustmentPath, defaultAdjustment);
  };

  // Add metric filter: initialize minimal shape per MetricFilterSchema
  const addMetricFilter = () => {
    const defaultFilter = {
      operator: MetricFilterOperatorSchema.enum['>'],
    } as PathValue<T, typeof metricFilterPath>;
    setValue(metricFilterPath, defaultFilter);
  };

  // Disable add when:
  // - a singleton (Segment/Business Unit) is present; OR
  // - total rows reached the max count of non-singleton types
  const categoricalFilters = (block.categoricalfilters || []) as Array<{ type?: string }>;
  const shouldDisableAddCategoricalFilter = isAddCategoricalDisabled(categoricalFilters);

  // Determine if this block can be deleted (not first block and there are multiple blocks)
  const canDeleteBlock = group.ruleBlocks.length > 1 && blockIndex > 0;

  return (
    <Box
      sx={{
        border: '2px dashed #d0d0d0',
        borderRadius: '4px',
        padding: '16px',
        marginBottom: '16px',
        backgroundColor: colors.lightestGrey
      }}
    >
      <Flex justifyContent="space-between" alignItems="center" style={{ marginBottom: '16px' }}>
        <Flex alignItems="center" gap={8}>
          <ExpandCollapseIcon isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
          <Text variant="h6" sx={{ fontWeight: 500 }}>
            Rule Block {blockIndex + 1}
          </Text>
        </Flex>

        <Flex alignItems="center" gap={8}>
          {/* Summary Display */}
          <Box
            sx={{
              backgroundColor: '#e8f5e8',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px'
            }}
          >
            {block.metric && block.aggregation
              ? `${block.aggregation} of ${block.metric} for ${generateTimePeriodDescription(block.timeConfiguration)}`
              : 'Incomplete rule'}
          </Box>
          <DeleteIcon onDelete={deleteRuleBlock} tooltip="Delete rule block" show={canDeleteBlock} />
        </Flex>
      </Flex>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <>
          {/* First Row: Metric Name, Aggregation Type, Include in Output */}
          <Flex gap={16} style={{ marginBottom: '16px' }} alignItems="flex-start">
            
              <PathControlledDropdown
                name={`${basePath}.metric` as Path<T>}
                label="Metric Name"
                options={metricsOptions}
                placeholder="Search for a metric..."
                sx={{
                  flex: 1,
                  maxWidth: DROPDOWN_WIDTH
                }}
              />
            
              <PathControlledDropdown
                name={`${basePath}.aggregation` as Path<T>}
                label="Aggregation Type"
                options={AggregationsSchema.options.filter((opt: AggregationsType) => opt !== 'none')}
                placeholder="Select an aggregation"
                sx={{
                  flex: 1,
                  maxWidth: DROPDOWN_WIDTH
                }}
              />
          </Flex>

          {/* Second Row: Time Range Type, Time Period */}
          <RuleBlockTimeRange<T> groupIndex={groupIndex} blockIndex={blockIndex} />

          <CategoricalFilterSection<T> groupIndex={groupIndex} blockIndex={blockIndex} />
          {hasMetricAdjustment && <MetricAdjustmentSection<T> groupIndex={groupIndex} blockIndex={blockIndex} />}
          {hasMetricFilter && <MetricFilterSection<T> groupIndex={groupIndex} blockIndex={blockIndex} />}

          <Divider sx={{ marginTop: '16px', marginBottom: '16px', borderColor: colors.graysSecondary }} />

          {/* Action Buttons */}
          <Flex gap={16} style={{ marginTop: '16px' }}>
            <RuleButton onClick={addCategoricalFilter} disabled={shouldDisableAddCategoricalFilter}>
              + Add Categorical Filter
            </RuleButton>
            <RuleButton onClick={addMetricAdjustment} disabled={hasMetricAdjustment}>
              + Add Metric Adjustment
            </RuleButton>
            <RuleButton onClick={addMetricFilter} disabled={hasMetricFilter}>
              + Add Metric Filter
            </RuleButton>
          </Flex>

          {shouldShowMetricSelectionError && (
            <Box sx={{ marginTop: '12px' }}>
            <Text
              variant="body3"
              sx={{
                color: colors.errorRed,
                fontSize: '12px',
                padding: '8px 12px',
                borderRadius: '4px',
                display: 'block'
              }}
            >
              When granularity includes &lsquo;Searchterm&rsquo;, the metric must be sensical to keyword.
            </Text>
          </Box>
          )}

          {/* Show validation error for 'For the First Time' mode without metric filter */}
          {shouldShowMetricFilterError && (
            <Box sx={{ marginTop: '12px' }}>
              <Text
                variant="body3"
                sx={{
                  color: colors.errorRed,
                  fontSize: '12px',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  display: 'block'
                }}
              >
                Metric filter is required when Time Range Type is &apos;For the First Time&apos;
              </Text>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default RuleBlock;
